import { Action, createReducer, on } from '@ngrx/store';
import * as NotificationsActions from '../actions';
import { Notification, NotificationPreferenceResponse } from '../../models/Notifications';

export interface NotificationsState {
  notifications: Notification[]; // Holds the current page of notifications
  nextPageUrl: string | null; // Stores the "next" URL from API response
  prevPageUrl: string | null; // Stores the "previous" URL from API response
  filter: string; // Stores the current filter (e.g., "success", "error")
  searchQuery: string; // Stores the current search query
  totalCount: number;
  preferences: NotificationPreferenceResponse[]; // Notification preferences
  /**
   * IDs the server has confirmed as read this session. A list requested before a
   * mark was saved can arrive after its confirmation, and reads never revert, so
   * these override `is_read` on every list that comes in.
   */
  confirmedReadIds: number[];
}

const initialState: NotificationsState = {
  notifications: [],
  nextPageUrl: null,
  prevPageUrl: null,
  filter: '',
  searchQuery: '',
  totalCount: 0,
  preferences: [],
  confirmedReadIds: [],
};

function withConfirmedReads(notifications: Notification[], confirmedReadIds: number[]): Notification[] {
  const confirmed = new Set(confirmedReadIds);
  return notifications.map((n) => (confirmed.has(n.id) && !n.is_read ? { ...n, is_read: true } : n));
}

export const notificationsReducer = createReducer(
  initialState,
  on(NotificationsActions.NotificationsApiActions.GetNotificationsSuccess, (state, { payload }) => ({
    ...state,
    notifications: withConfirmedReads(payload.results, state.confirmedReadIds),
    nextPageUrl: payload.next,
    prevPageUrl: payload.previous,
    totalCount: payload.count,
  })),
  on(NotificationsActions.NotificationsApiActions.GetNotificationsFailure, (state) => ({
    ...state,
  })),
  on(NotificationsActions.NotificationsApiActions.MarkNotificationsReadSuccess, (state, { ids }) => {
    const confirmedReadIds = [...new Set([...state.confirmedReadIds, ...ids])];
    return {
      ...state,
      confirmedReadIds,
      notifications: withConfirmedReads(state.notifications, confirmedReadIds),
    };
  }),
  on(NotificationsActions.NotificationsApiActions.GetNotificationPreferencesSuccess, (state, { payload }) => ({
    ...state,
    preferences: payload,
  })),
  on(NotificationsActions.NotificationsApiActions.GetNotificationPreferencesFailure, (state) => ({
    ...state,
  })),
  on(NotificationsActions.NotificationsApiActions.UpdateNotificationPreferencesSuccess, (state, { payload }) => {
    // Handle both array response and object with updated/errors
    const updatedPreferences = Array.isArray(payload) ? payload : payload.updated || [];
    return {
      ...state,
      preferences: updatedPreferences.length > 0 ? updatedPreferences : state.preferences,
    };
  }),
  on(NotificationsActions.NotificationsApiActions.UpdateNotificationPreferencesFailure, (state) => ({
    ...state,
  }))
);

export function LabsReducer(state: NotificationsState | undefined, action: Action) {
  return notificationsReducer(state, action);
}
